// src/pages/ia-paca/marseille.tsx
//
// Page géo principale — Marseille.
// Stratégie : capter "recrutement IA Marseille", "AI Engineer
// Marseille", "intelligence artificielle Marseille emploi".
// Ancrage local fort : CMA CGM × Mistral, La Coque, AIM 2026,
// French Tech Aix-Marseille.

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Layout from '../../components/layout/layout';
import IaPacaHero from '../../components/ia-paca/IaPacaHero';
import IaPacaCTA from '../../components/ia-paca/IaPacaCTA';
import IaSalaryTable from '../../components/ia-paca/IaSalaryTable';
import {
  ArrowLeft,
  ArrowRight,
  Anchor,
  Ship,
  Building2,
  Rocket,
  MapPin,
} from 'lucide-react';

export default function MarseilleIaPage() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'Recruteurs.io — Recrutement IA à Marseille',
    description:
      "Cabinet de recrutement IA basé à Marseille. Profils AI Engineer, MLOps, Data Scientist, Consultant IA pour les startups, scale-ups et PME marseillaises en transformation IA.",
    url: 'https://recruteurs.io/ia-paca/marseille',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Marseille',
      addressRegion: "Provence-Alpes-Côte d'Azur",
      addressCountry: 'FR',
    },
    areaServed: { '@type': 'City', name: 'Marseille' },
  };

  return (
    <Layout
      title="Recrutement IA à Marseille — AI Engineer, MLOps, Data Scientist | recruteurs.io"
      description="Cabinet de recrutement IA à Marseille. Profils AI Engineer, MLOps, Data Scientist pour les scale-ups marseillaises, la French Tech Aix-Marseille, et les PME en transformation IA. Marché local, salaires réels, sourcing ciblé."
      keywords="recrutement IA Marseille, AI Engineer Marseille, intelligence artificielle Marseille emploi, recrutement MLOps Marseille, cabinet recrutement tech Marseille, recruter data scientist Marseille"
      breadcrumbs={[
        { name: 'Accueil', href: '/' },
        { name: 'Recrutement IA PACA', href: '/ia-paca' },
        { name: 'Marseille', href: '/ia-paca/marseille' },
      ]}
    >
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <IaPacaHero
        eyebrow="Recrutement IA · Marseille"
        title="Recruter en IA"
        highlight="à Marseille."
        subtitle="Cabinet de recrutement basé à Marseille. AI Engineer pour scale-up de la French Tech Aix-Marseille, Data Scientist pour PME en transfo IA, Consultant IA pour les fonds de la place. On connaît le terrain local : équipes, salaires, contre-offres."
        badges={['French Tech Aix-Marseille', 'La Coque', 'CMA CGM × Mistral', 'AIM 2026']}
      />

      <div className="bg-white pt-6">
        <div className="mx-auto max-w-4xl px-6">
          <Link
            to="/ia-paca"
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft className="h-3 w-3" />
            Toutes les villes IA en PACA
          </Link>
        </div>
      </div>

      {/* Marché tech local 2026 */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Marché tech & IA — Marseille 2026
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Marseille n'est plus un suiveur tech. C'est un hub IA.
          </h2>
          <div className="prose prose-slate mt-6 max-w-none text-base leading-relaxed text-slate-700">
            <p>
              En 2026, Marseille concentre une des dynamiques IA les plus
              actives de France. Le partenariat <strong>CMA CGM × Mistral AI
              à 100 M€ sur 5 ans</strong> (annoncé en 2025) a positionné la
              ville comme un point d'ancrage de l'IA logistique et maritime.
              Autour de ça, un écosystème entier se structure : startups
              DeepTech, scale-ups financées par la French Tech Aix-Marseille,
              PME industrielles en transformation IA générative.
            </p>
            <p>
              La <strong>4ème édition d'AIM 2026</strong> (Artificial
              Intelligence Marseille) à l'Orange Vélodrome confirme que la
              région ne se limite plus à Sophia Antipolis pour l'IA. Marseille
              construit son propre récit — plus appliqué, plus business, plus
              ancré dans les secteurs locaux (logistique, santé, défense,
              tourisme, retail).
            </p>
          </div>
        </div>
      </section>

      {/* Acteurs IA à Marseille */}
      <section className="bg-slate-50 py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Qui recrute en IA à Marseille ?
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Les profils types des entreprises qui nous appellent.
          </h2>
          <p className="mt-3 text-base text-slate-600">
            Sans nommer nos clients, voici les 4 typologies d'entreprises
            marseillaises qui recrutent en IA en 2026.
          </p>

          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
            <ActeurCard
              Icon={Ship}
              tag="Maritime & Logistique"
              title="Scale-ups et corporates de la place portuaire"
              body="CMA CGM, Bourbon, Marseille Gyptis International, Aplix... La filière logistique investit massivement dans l'IA pour l'optimisation de routes, la maintenance prédictive, et le traitement automatisé de documents (BL, douane, ETA)."
              profils="AI Engineer, MLOps, Data Engineer, Computer Vision"
            />
            <ActeurCard
              Icon={Rocket}
              tag="Scale-ups & Startups"
              title="French Tech Aix-Marseille en croissance"
              body="Une trentaine de scale-ups marseillaises (Sunday, Boris Tech, Aprolis, Lectaurs, etc.) recrutent en IA pour scaler leurs produits. Stack jeune (LLM, RAG, agents), équipes de 5-50, niveau d'exigence technique élevé."
              profils="AI Engineer, Full-stack IA, Prompt Engineer, AI PM"
            />
            <ActeurCard
              Icon={Building2}
              tag="PME en transformation"
              title="PME 50-500 salariés qui automatisent"
              body="Cabinets d'experts comptables, agences immo, distribution, agriculture, hôtellerie... Les PME marseillaises automatisent leurs back-offices avec n8n + LLM, ou intègrent des copilotes métiers."
              profils="AI Automation Specialist, Consultant IA, Développeur LLM"
            />
            <ActeurCard
              Icon={Anchor}
              tag="DeepTech & Recherche"
              title="Spin-offs académiques et CNRS"
              body="AMU (Aix-Marseille Université), IFREMER, INSERM, et leurs incubateurs (Impulse, Belle de Mai) génèrent des spin-offs IA dans la santé, l'océanographie, la biotech. Profils PhD-friendly, exigence scientifique élevée."
              profils="ML Research Engineer, Data Scientist PhD, MLOps recherche"
            />
          </div>
        </div>
      </section>

      {/* Pôles & écosystème */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Écosystème local — où tout se passe
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Les hubs, accélérateurs et événements qu'on suit.
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
            {ECOSYSTEME.map((e) => (
              <div
                key={e.nom}
                className="rounded-2xl border border-slate-200 bg-white p-5"
              >
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-pink-600" />
                  <h3 className="text-base font-bold text-slate-900">{e.nom}</h3>
                </div>
                <p className="mt-2 text-sm text-slate-600">{e.description}</p>
                {e.quartier && (
                  <p className="mt-2 text-[11px] text-slate-500">
                    📍 {e.quartier}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Salaires IA Marseille */}
      <section className="bg-slate-50 py-12 lg:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Salaires IA Marseille — mars 2026
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Combien on paie un profil IA à Marseille ?
          </h2>
          <p className="mt-3 text-base text-slate-600">
            Données réelles compilées sur les 6 derniers mois. Marseille est
            globalement à -10% de Paris, sauf pour les scale-ups financées
            (alignées sur Paris pour rester compétitives).
          </p>
          <div className="mt-8">
            <IaSalaryTable />
          </div>
        </div>
      </section>

      {/* Métiers IA recrutés à Marseille */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Profils que nous recrutons à Marseille
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Tous les métiers IA — recherche locale + relocalisation.
          </h2>

          <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {METIERS_MARSEILLE.map((m) => (
              <Link
                key={m.slug}
                to={`/ia-paca/metiers/${m.slug}`}
                className="group flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4 transition hover:border-violet-300 hover:shadow-md"
              >
                <div>
                  <h3 className="text-sm font-bold text-slate-900 group-hover:text-violet-700">
                    {m.label}
                  </h3>
                  <p className="mt-0.5 text-[11px] text-slate-500">{m.pitch}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-violet-600" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ géo */}
      <section className="bg-slate-50 py-12 lg:py-16">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            FAQ Marseille
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Les questions qu'on nous pose souvent.
          </h2>
          <div className="mt-8 space-y-3">
            {FAQ_MARSEILLE.map((q) => (
              <details
                key={q.question}
                className="group rounded-2xl border border-slate-200 bg-white p-5"
              >
                <summary className="flex cursor-pointer items-start justify-between gap-4 text-base font-semibold text-slate-900 [&::-webkit-details-marker]:hidden">
                  <span>{q.question}</span>
                  <span className="mt-0.5 text-slate-400 transition group-open:rotate-45">
                    +
                  </span>
                </summary>
                <div className="mt-4 text-sm leading-relaxed text-slate-600">
                  {q.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Sophia / autres villes */}
      <section className="bg-white py-12">
        <div className="mx-auto max-w-4xl px-6">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <div className="flex items-center gap-3">
              <MapPin className="h-5 w-5 text-pink-600" />
              <div>
                <h3 className="text-base font-bold text-slate-900">
                  Vous recrutez aussi ailleurs en PACA ?
                </h3>
                <p className="mt-1 text-sm text-slate-600">
                  <Link
                    to="/ia-paca/sophia-antipolis"
                    className="font-semibold text-pink-700 hover:underline"
                  >
                    Sophia Antipolis
                  </Link>
                  ,{' '}
                  <Link
                    to="/ia-paca"
                    className="font-semibold text-pink-700 hover:underline"
                  >
                    et toute la région PACA
                  </Link>
                  .
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <IaPacaCTA
        title="Vous recrutez un profil IA à Marseille ?"
        subtitle="Cabinet basé à Marseille, présent sur tous les événements locaux (AIM, French Tech, Devfest). On peut venir vous voir dans vos bureaux. Brief 30 min, short-list en 15 jours."
      />
    </Layout>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────

function ActeurCard({
  Icon,
  tag,
  title,
  body,
  profils,
}: {
  Icon: typeof Ship;
  tag: string;
  title: string;
  body: string;
  profils: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 transition hover:border-violet-300 hover:shadow-md">
      <div className="flex items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-pink-50 text-pink-600">
          <Icon className="h-5 w-5" />
        </div>
        <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-violet-700">
          {tag}
        </span>
      </div>
      <h3 className="mt-3 text-base font-bold text-slate-900">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
      <div className="mt-3 border-t border-slate-100 pt-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
          Profils types
        </div>
        <p className="mt-1 text-xs text-slate-700">{profils}</p>
      </div>
    </div>
  );
}

const ECOSYSTEME = [
  {
    nom: 'La Coque',
    description:
      "Coworking emblématique du quartier Saint-Charles, point de ralliement des startups et freelances tech marseillais. Events réguliers IA, recrutement, growth.",
    quartier: 'Saint-Charles / Belsunce',
  },
  {
    nom: 'French Tech Aix-Marseille',
    description:
      "Communauté officielle French Tech qui anime l'écosystème startup. Programme d'accélération, mentorat, mise en relation avec les fonds.",
    quartier: 'Marseille + Aix',
  },
  {
    nom: 'AIM — Artificial Intelligence Marseille',
    description:
      "Conférence annuelle IA de la place, 4ème édition en septembre à l'Orange Vélodrome. Le rendez-vous où se croisent CTO, fondateurs, fonds, candidats.",
    quartier: 'Orange Vélodrome',
  },
  {
    nom: 'P.Factory',
    description:
      "Accélérateur de Provence-Alpes Côte d'Azur orienté startups industrielles, deep tech, IA appliquée. Forte présence d'anciens cadres industriels.",
    quartier: 'Marseille',
  },
  {
    nom: 'Pôle SCS (compétitivité)',
    description:
      "Pôle de compétitivité Solutions Communicantes Sécurisées. Met en relation grandes entreprises, PME et labos sur l'IA, IoT, cybersécurité.",
    quartier: 'Sophia + Marseille',
  },
  {
    nom: 'Belle de Mai (Pôle Média)',
    description:
      "Incubateur historique de la friche La Belle de Mai. Spécialisé deeptech, médias, jeux vidéo. Beaucoup de startups IA appliquée.",
    quartier: 'La Belle de Mai',
  },
];

const METIERS_MARSEILLE = [
  { slug: 'ai-engineer', label: 'AI Engineer', pitch: 'RAG, agents, LLM en prod.' },
  {
    slug: 'machine-learning-engineer',
    label: 'ML Engineer',
    pitch: 'Pipeline ML industrialisé.',
  },
  {
    slug: 'data-scientist',
    label: 'Data Scientist',
    pitch: 'Modèles, exploration, insight.',
  },
  {
    slug: 'mlops-engineer',
    label: 'MLOps Engineer',
    pitch: 'Infra GPU, monitoring modèles.',
  },
  {
    slug: 'ai-automation-specialist',
    label: 'AI Automation Specialist',
    pitch: 'n8n + LLM, workflows métier.',
  },
  {
    slug: 'consultant-ia',
    label: 'Consultant IA',
    pitch: 'Accompagne la transfo IA.',
  },
  {
    slug: 'data-engineer-ia',
    label: 'Data Engineer',
    pitch: 'Pipelines data pour IA.',
  },
  {
    slug: 'ai-product-manager',
    label: 'AI Product Manager',
    pitch: 'Pilote produit IA.',
  },
  {
    slug: 'prompt-engineer',
    label: 'Prompt Engineer',
    pitch: 'Eval, guardrails, A/B prompts.',
  },
];

const FAQ_MARSEILLE = [
  {
    question: "Combien d'AI Engineers seniors disponibles à Marseille en 2026 ?",
    answer:
      "Notre estimation : entre 80 et 150 AI Engineers seniors (5+ ans) actifs sur Marseille et son agglo. Sur ce pool, environ 30-40% sont en veille active ou ouverts à une opportunité. Pour comparer : Paris en a ~3000, Sophia Antipolis environ 200-300. C'est un marché tendu mais qui bouge.",
  },
  {
    question:
      "Quelle différence salariale entre Marseille et Paris pour un profil IA ?",
    answer:
      "Globalement -10 à -15% sur Marseille vs Paris pour le même profil. MAIS les scale-ups marseillaises bien financées (CMA CGM Group, levées récentes French Tech) s'alignent sur Paris pour attirer les profils. Pour les PME, l'écart se justifie par le coût de vie (-25% logement vs Paris).",
  },
  {
    question:
      "Recrutez-vous des profils en remote ou uniquement présentiel à Marseille ?",
    answer:
      "Les deux. Beaucoup d'entreprises marseillaises proposent du remote partiel (2-3 jours sur site). Pour les profils IA seniors qui veulent du 100% remote, c'est possible mais l'entreprise doit l'assumer dès le brief — sinon on perd du temps en short-list.",
  },
  {
    question:
      "Pourquoi un AI Engineer parisien viendrait travailler à Marseille ?",
    answer:
      "Les 3 raisons qui reviennent : (1) qualité de vie réelle (mer, soleil, +12°C en moyenne, prix logement -40%), (2) projets IA appliqués concrets (vs Paris où beaucoup tournent en POC), (3) écosystème en construction = plus d'opportunités de leadership précoce. C'est devenu un argument que les recruteurs marseillais peuvent vendre sérieusement en 2026.",
  },
  {
    question:
      "Mon entreprise est à Aubagne / La Ciotat / Vitrolles — vous intervenez aussi ?",
    answer:
      "Oui. Notre périmètre Marseille couvre toute la métropole AMP (Aix-Marseille-Provence) : Aubagne, La Ciotat, Vitrolles, Marignane, Martigues, Salon, Istres. On recrute aussi pour les entreprises basées dans les Bouches-du-Rhône (13) et adjacents.",
  },
];
